// Supabase email template must use this redirect URL format:
// {{ .SiteURL }}/confirm-email?token_hash={{ .TokenHash }}&type=email
// Configure in: Supabase → Authentication → Email → Templates → Confirm signup
// This intermediate page prevents email pre-fetchers from consuming the token
// before the user clicks the confirmation button.

import React, { useCallback, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import Logo from '../components/Logo';
import { Spinner } from '../components/Spinner';
import { supabase } from '../lib/supabase';

type FlowState = 'ready' | 'confirming' | 'error';

type ErrorKind = 'missing_params' | 'expired' | 'used' | 'generic';

function classifyAuthError(message: string): Exclude<ErrorKind, 'missing_params'> {
  const m = message.toLowerCase();
  if (m.includes('expired')) return 'expired';
  if (
    m.includes('already been used') ||
    m.includes('already used') ||
    m.includes('has already been') ||
    m.includes('otp has already') ||
    m.includes('one-time token')
  ) {
    return 'used';
  }
  return 'generic';
}

const ALLOWED_OTP_TYPES = ['signup', 'email', 'recovery', 'invite', 'email_change', 'magiclink'] as const;
type AllowedOtpType = (typeof ALLOWED_OTP_TYPES)[number];

function parseOtpType(raw: string | null): AllowedOtpType | null {
  if (!raw) return null;
  return (ALLOWED_OTP_TYPES as readonly string[]).includes(raw) ? (raw as AllowedOtpType) : null;
}

function IconEnvelope48() {
  return (
    <svg
      width={48}
      height={48}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="mx-auto"
      style={{ color: 'var(--accent-green)' }}
      aria-hidden
    >
      <rect x="3" y="5" width="18" height="14" rx="2" stroke="currentColor" strokeWidth="1.75" />
      <path
        d="M3 7l9 6 9-6"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconX48() {
  return (
    <svg
      width={48}
      height={48}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="mx-auto"
      style={{ color: 'var(--accent-danger)' }}
      aria-hidden
    >
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.75" />
      <path
        d="M8 8l8 8M16 8L8 16"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
      />
    </svg>
  );
}

const cardStyle: React.CSSProperties = {
  maxWidth: 440,
  borderRadius: 16,
  padding: 40,
  backgroundColor: 'var(--bg-surface)',
  border: '1px solid var(--border-color)',
  boxShadow: 'var(--modal-panel-shadow)',
};

const primaryButtonStyle: React.CSSProperties = {
  width: '100%',
  backgroundColor: 'var(--accent-green)',
  color: 'var(--text-on-accent)',
  padding: '14px 32px',
  borderRadius: 8,
  fontSize: 16,
  fontWeight: 600,
  border: 'none',
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 8,
};

export default function ConfirmEmailPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [flow, setFlow] = useState<FlowState>('ready');
  const [errorKind, setErrorKind] = useState<ErrorKind | null>(null);

  const handleConfirm = useCallback(async () => {
    const tokenHash = searchParams.get('token_hash')?.trim() ?? '';
    const typeRaw = searchParams.get('type')?.trim() ?? '';
    const otpType = parseOtpType(typeRaw);

    if (!tokenHash || !otpType) {
      setErrorKind('missing_params');
      setFlow('error');
      return;
    }

    setFlow('confirming');

    const { data, error } = await supabase.auth.verifyOtp({
      token_hash: tokenHash,
      type: otpType,
    });

    if (error) {
      setErrorKind(classifyAuthError(error.message));
      setFlow('error');
      return;
    }

    const user = data.user ?? data.session?.user ?? null;
    const email =
      typeof user?.email === 'string' && user.email.trim().length > 0 ? user.email.trim() : '';

    await supabase.auth.signOut();

    const loginUrl =
      email.length > 0
        ? `/login?confirmed=true&email=${encodeURIComponent(email)}`
        : '/login?confirmed=true';

    navigate(loginUrl, { replace: true });
  }, [navigate, searchParams]);

  const errorTitle =
    errorKind === 'expired'
      ? 'Link Expired'
      : errorKind === 'used'
        ? 'Link Already Used'
        : errorKind === 'missing_params'
          ? 'Invalid Link'
          : 'Invalid Link';

  const errorBody =
    errorKind === 'expired'
      ? 'This confirmation link has expired. Please sign up again to receive a new link.'
      : errorKind === 'used'
        ? 'This link has already been used. If you have not confirmed your email yet, please sign up again.'
        : errorKind === 'missing_params'
          ? 'Invalid confirmation link'
          : 'Something went wrong. Please contact hello@lognsec.com for assistance.';

  return (
    <main className="min-h-screen bg-navy px-4 py-10 text-foreground sm:px-6 sm:py-14">
      <div className="mx-auto w-full" style={{ maxWidth: 440 }}>
        <div style={cardStyle}>
          <div className="mb-6 flex justify-center">
            <Logo size="lg" />
          </div>

          {flow === 'error' && errorKind ? (
            <>
              <IconX48 />
              <h1 className="mt-6 text-center text-xl font-semibold text-foreground">{errorTitle}</h1>
              <p className="mt-3 text-center text-sm leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
                {errorBody}
              </p>
              <Link
                to="/login"
                className="mt-8 flex w-full min-h-[48px] items-center justify-center rounded-lg px-4 py-3 text-sm font-semibold text-cta-text transition hover:opacity-90"
                style={{ backgroundColor: 'var(--accent-green)' }}
              >
                Back to Login
              </Link>
              <p className="mt-4 text-center text-sm">
                <Link to="/signup" className="font-medium text-accent underline-offset-2 hover:underline">
                  Sign up again
                </Link>
              </p>
            </>
          ) : (
            <>
              <IconEnvelope48 />
              <h1 className="mt-6 text-center text-xl font-semibold text-foreground">
                Confirm Your Email Address
              </h1>
              <p
                className="mt-3 text-center text-sm leading-relaxed"
                style={{ color: 'var(--text-secondary)' }}
              >
                Click the button below to verify your email and activate your account. This step protects
                your account from unauthorized access.
              </p>
              <button
                type="button"
                disabled={flow === 'confirming'}
                onClick={() => void handleConfirm()}
                style={{
                  ...primaryButtonStyle,
                  marginTop: 24,
                  opacity: flow === 'confirming' ? 0.85 : 1,
                  cursor: flow === 'confirming' ? 'not-allowed' : 'pointer',
                }}
              >
                {flow === 'confirming' ? (
                  <>
                    <Spinner className="h-5 w-5 text-cta-text" />
                    Confirming...
                  </>
                ) : (
                  'Confirm My Email'
                )}
              </button>
              <p
                className="mt-4 text-center"
                style={{ fontSize: 12, color: 'var(--text-tertiary)' }}
              >
                This link expires in 1 hour
              </p>
            </>
          )}
        </div>
      </div>
    </main>
  );
}
